import React, { useMemo } from 'react';

const TimeStatsViewer = React.memo(function TimeStatsViewer({
  table, dateCol, catCol, searchIndices, minX, maxX, isLogScale, colorConfig, includeMissingDates
}) {
  const { plotData, categories, maxTotal, finalTotals } = useMemo(() => {
    if (!table || !dateCol || !catCol) return { plotData: [], categories: [], maxTotal: 0, finalTotals: {} };
    const dateData = table.getChild(dateCol);
    const catData = table.getChild(catCol);
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

    const catCounts = new Map();
    const validEvents = [];
    const baselineTotals = {};

    for (let i = 0; i < source.length; i++) {
      const rowIdx = source[i];
      const cVal = catData.get(rowIdx);
      const isNull = cVal == null || cVal === '';
      const cStr = isNull ? 'null' : String(cVal);
      
      catCounts.set(cStr, (catCounts.get(cStr) || 0) + 1);

      const tsVal = dateData.get(rowIdx);
      const ts = new Date(tsVal).getTime();
      
      // Handle Missing Dates independently
      if (isNaN(ts) || ts === 0) {
        if (includeMissingDates) {
          baselineTotals[cStr] = (baselineTotals[cStr] || 0) + 1;
        }
      } else {
        validEvents.push({ x: ts, c: cStr });
      }
    }

    // Top 15 + Other
    const sortedCats = Array.from(catCounts.entries()).sort((a, b) => b[1] - a[1]);
    const topCats = sortedCats.slice(0, 15).map(d => d[0]);
    const topCatsSet = new Set(topCats);
    if (sortedCats.length > 15) topCats.push('Other');

    validEvents.sort((a, b) => a.x - b.x);

    const runningTotals = {};
    topCats.forEach(c => runningTotals[c] = 0);
    let currentTotal = 0;

    // Apply baseline missing dates to the running totals first
    if (includeMissingDates) {
      Object.entries(baselineTotals).forEach(([cStr, count]) => {
        const c = topCatsSet.has(cStr) ? cStr : 'Other';
        runningTotals[c] += count;
        currentTotal += count;
      });
    }

    const plotData = [];
    
    // Add a synthetic starting point to guarantee a baseline spans off-screen or from minX
    const firstX = validEvents.length > 0 ? Math.min(minX, validEvents[0].x) : minX;
    plotData.push({ x: firstX - 1, totals: { ...runningTotals }, total: currentTotal });

    validEvents.forEach((ev, idx) => {
      const c = topCatsSet.has(ev.c) ? ev.c : 'Other';
      runningTotals[c]++;
      currentTotal++;

      // Only save data points when x changes
      if (idx === validEvents.length - 1 || validEvents[idx + 1].x !== ev.x) {
        plotData.push({ x: ev.x, totals: { ...runningTotals }, total: currentTotal });
      }
    });

    return { plotData, categories: topCats, maxTotal: currentTotal, finalTotals: runningTotals };
  }, [table, dateCol, catCol, searchIndices, includeMissingDates, minX]);

  const maxY = maxTotal || 1;
  const getNormX = (timestamp) => ((timestamp - minX) / (maxX - minX || 1)) * 100;
  
  const getNormY = (count) => {
    if (isLogScale) return 100 - (Math.log10(count + 1) / Math.log10(maxY + 1)) * 100;
    return 100 - (count / maxY) * 100;
  };

  const getCatPath = (catIdx) => {
    const topPoints = [];
    const botPoints = [];
    let lastX = plotData[0].x;
    let lastTop = 0;
    let lastBot = 0;

    plotData.forEach(p => {
      let bot = 0;
      for (let i = 0; i < catIdx; i++) bot += p.totals[categories[i]] || 0;
      let top = bot + (p.totals[categories[catIdx]] || 0);

      if (topPoints.length > 0) {
          // Step chart progression
          topPoints.push([p.x, lastTop]);
          botPoints.push([p.x, lastBot]);
      }
      topPoints.push([p.x, top]);
      botPoints.push([p.x, bot]);

      lastX = p.x; lastTop = top; lastBot = bot;
    });

    if (lastX < maxX) {
      topPoints.push([maxX, lastTop]);
      botPoints.push([maxX, lastBot]);
    }

    if (topPoints.length === 0) return '';

    let d = `M ${getNormX(topPoints[0][0])} ${getNormY(topPoints[0][1])}`;
    for (let i = 1; i < topPoints.length; i++) d += ` L ${getNormX(topPoints[i][0])} ${getNormY(topPoints[i][1])}`;
    for (let i = botPoints.length - 1; i >= 0; i--) d += ` L ${getNormX(botPoints[i][0])} ${getNormY(botPoints[i][1])}`;
    return d + ' Z';
  };

  const getEntryColor = (val) => {
    if (val === 'Other') return 'rgba(0, 0, 0, 0.32)';
    if (!colorConfig) return 'rgba(100,100,100,0.5)';
    const entry = [...colorConfig.colorMap.entries()].find(([k]) => String(k) === String(val));
    const rgba = entry ? entry[1] : [100, 100, 100, 200];
    
    // Use the exact same alpha scaling as StatsViewer to get the correct muted null color
    return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${Math.max(rgba[3] / 255, 0.1)})`;
  };

  const levels = [1, 0.75, 0.5, 0.25, 0];
  const formatY = (val) => val >= 1000 ? (val / 1000).toFixed(1) + 'k' : Math.round(val);

  return (
    <div className="flex flex-row items-stretch w-full h-full overflow-hidden">
      <div className="flex-1 flex flex-col min-w-0 pr-2">
        <div className="flex flex-row flex-grow min-h-0 relative">
           <div className="relative w-8 border-r border-gray-200 text-gray-600 text-[8px] shrink-0">
              {levels.map(p => (
                <div key={p} className="absolute right-1 -translate-y-1 text-right whitespace-nowrap" style={{ top: `${(1 - p) * 100}%` }}>
                  {formatY(isLogScale ? (Math.pow(10, p * Math.log10(maxY + 1)) - 1) : (maxY * p))}
                </div>
              ))}
           </div>
           <div className="relative flex-grow bg-white overflow-hidden">
              <div className="absolute inset-0 pointer-events-none opacity-30">
                {levels.map(p => (
                  <div key={p} className={`absolute w-full ${p!=0? "border-t border-gray-400 border-dashed":"border-t border-blue-400"}`} style={{ top: `${(1 - p) * 100}%` }} />
                ))}
              </div>
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                {categories.map((c, i) => (
                  <path key={c} d={getCatPath(i)} fill={getEntryColor(c)} stroke="#fff" strokeWidth="0.1" vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
           </div>
        </div>
        <div className="h-4 flex flex-row mt-1 shrink-0">
          <div className="w-8 shrink-0" />
          <div className="flex-grow flex justify-between px-1 text-[9px] text-gray-600">
            <span>{new Date(minX).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
            <span>{new Date(maxX).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
      </div>

      <div className="basis-1/4 shrink-0 flex flex-col overflow-y-auto pl-0 pr-0 pt-1 min-w-0">
         <div className="my-auto w-full">
           {categories.map((c) => (
             <div key={c} title={c} className="flex items-center text-[10px] gap-1 mb-1.5 w-full">
                <div className="w-3 h-3 rounded-sm shrink-0 border border-gray-800" style={{ backgroundColor: getEntryColor(c) }} />
                <span className="truncate flex-grow text-gray-700 font-semibold">{c}</span>
             </div>
           ))}
         </div>
      </div>
    </div>
  );
});

export default TimeStatsViewer;